import type { HealthUpdateCheckResult } from "./index.js";

type NpmPackageMetadata = {
  version?: string;
};

function parseVersion(input: string): {
  parts: number[];
  prerelease?: string;
} {
  const [coreVersion, prerelease] = input.split("-", 2);
  return {
    parts: coreVersion.split(".").map((segment) => {
      const parsed = Number.parseInt(segment, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }),
    prerelease,
  };
}

function compareVersions(left: string, right: string): number {
  const leftVersion = parseVersion(left);
  const rightVersion = parseVersion(right);
  const length = Math.max(leftVersion.parts.length, rightVersion.parts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftVersion.parts[index] ?? 0;
    const rightPart = rightVersion.parts[index] ?? 0;
    if (leftPart !== rightPart) {
      return leftPart > rightPart ? 1 : -1;
    }
  }

  if (!leftVersion.prerelease && !rightVersion.prerelease) {
    return 0;
  }
  if (!leftVersion.prerelease) {
    return 1;
  }
  if (!rightVersion.prerelease) {
    return -1;
  }

  return leftVersion.prerelease.localeCompare(rightVersion.prerelease);
}

export async function checkLatestPackageVersion(
  packageName: string,
  currentVersion: string,
): Promise<HealthUpdateCheckResult> {
  const controller = new AbortController();
  const timeoutHandle = setTimeout(() => controller.abort(), 3000);

  try {
    const response = await fetch(
      `https://registry.npmjs.org/${encodeURIComponent(packageName)}/latest`,
      {
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      return {
        status: "unavailable",
        detail: `npm registry request failed with status ${response.status}`,
      };
    }

    const payload = (await response.json()) as NpmPackageMetadata;
    const latestVersion = payload.version?.trim();
    if (!latestVersion) {
      return {
        status: "unavailable",
        detail: "npm registry response did not include a latest version",
      };
    }

    const comparison = compareVersions(currentVersion, latestVersion);
    return {
      latestVersion,
      status: comparison < 0 ? "update-available" : "up-to-date",
      detail:
        comparison > 0
          ? "installed version is newer than the npm latest tag"
          : undefined,
    };
  } catch (error) {
    return {
      status: "unavailable",
      detail: error instanceof Error ? error.message : String(error),
    };
  } finally {
    clearTimeout(timeoutHandle);
  }
}
